import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sun, Flame, Zap, ZoomIn } from 'lucide-react';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';
import type { LocalizedText } from '../../shared/types';
import Lightbox from '../components/Lightbox';

interface PainTechSectionProps {
  content: Record<string, unknown>;
}

interface TechItem {
  icon: string;
  title: LocalizedText;
  desc: LocalizedText;
}

const iconMap: Record<string, React.ElementType> = {
  'red-light': Sun,
  'stone-heat': Flame,
  vibration: Zap,
};

const PainTechSection: React.FC<PainTechSectionProps> = ({ content }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const painEyebrow = getLocalizedText(content.painEyebrow, lang, t('painTech.painEyebrow'));
  const painTitle = getLocalizedText(content.painTitle, lang, t('painTech.painTitle'));
  const painDesc = getLocalizedText(content.painDesc, lang, t('painTech.painDesc'));
  const painImage = (content.painImage as string) || '';
  const techEyebrow = getLocalizedText(content.techEyebrow, lang, t('painTech.techEyebrow'));
  const techTitle = getLocalizedText(content.techTitle, lang, t('painTech.techTitle'));
  const techItems = (content.techItems as TechItem[] | undefined) || [];

  return (
    <section id="pain-tech" className="relative py-12 md:py-16 bg-brand-paper overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
          {/* Pain block */}
          <motion.div
            className="bg-brand-stone p-8 md:p-12 border-l-4 border-brand-cinnabar"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2.5 text-brand-cinnabar font-sans tracking-[0.25em] text-sm md:text-base uppercase font-bold mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-cinnabar opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-cinnabar" />
              </span>
              {painEyebrow}
            </span>
            <h2 className="text-2xl md:text-3xl font-serif text-brand-ink mb-6">
              {painTitle}
            </h2>
            <p className="text-brand-ink/70 font-sans leading-relaxed text-base md:text-lg mb-8">
              {painDesc}
            </p>
            {painImage && (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative overflow-hidden rounded-sm border border-brand-ink/5 bg-brand-paper cursor-zoom-in group"
                  onClick={() => setLightboxOpen(true)}
                  role="button"
                  aria-label="放大查看圖片"
                >
                  <img
                    src={painImage}
                    alt=""
                    className="w-full max-h-[28rem] object-contain mx-auto"
                    loading="lazy"
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-brand-ink/70 text-brand-paper text-xs px-2.5 py-1.5 rounded-sm pointer-events-none">
                    <ZoomIn className="w-3.5 h-3.5" />
                    點擊放大
                  </div>
                </motion.div>
                <Lightbox
                  src={painImage}
                  isOpen={lightboxOpen}
                  onClose={() => setLightboxOpen(false)}
                />
              </>
            )}
          </motion.div>

          {/* Tech block */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-brand-gold font-sans tracking-[0.2em] text-xs uppercase mb-4 block">
              {techEyebrow}
            </span>
            <h2 className="text-2xl md:text-3xl font-serif text-brand-ink mb-8">
              {techTitle}
            </h2>
            <div className="space-y-6">
              {techItems.map((item, index) => {
                const Icon = iconMap[item.icon] || Sun;
                return (
                  <motion.div
                    key={item.icon}
                    className="flex gap-5 p-6 bg-white border border-brand-ink/5 hover:border-brand-cinnabar/20 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-ink text-brand-paper flex items-center justify-center">
                      <Icon className="w-5 h-5 text-brand-cinnabar" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-brand-ink mb-2">
                        {getLocalizedText(item.title, lang)}
                      </h3>
                      <p className="text-brand-ink/60 font-sans text-sm leading-relaxed">
                        {getLocalizedText(item.desc, lang)}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PainTechSection;
